import { iconeDe } from './apparence';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * « Comment ça marche » — une suite d'étapes numérotées.
 *
 * ========================================================================
 * LE NUMÉRO **ET** L'ICÔNE, PAS L'UN OU L'AUTRE
 * ========================================================================
 *
 * Une première version faisait remplacer le numéro par l'icône : la colonne
 * restait alignée, mais on perdait le rang. Or une procédure d'installation
 * se suit dans l'ordre — « je suis à l'étape 3 » est l'information la plus
 * utile, et l'icône ne fait que l'illustrer.
 *
 * La pastille dégradée porte donc le numéro, et l'icône vient à côté du titre
 * dans un carré gris. C'est la disposition de la page d'installation
 * d'origine, reproduite plutôt que réinventée.
 *
 * ========================================================================
 * CHAQUE ÉTAPE EST UNE CARTE, SÉPARÉE DE LA SUIVANTE
 * ========================================================================
 *
 * Empilées sans séparation, quatre étapes se lisent comme un seul paragraphe
 * long. Le trait de séparation et le survol qui éclaire la carte disent où
 * commence et où finit une étape.
 *
 * `astuce` et `image` restent facultatifs : les blocs écrits avant leur
 * existence s'affichent exactement comme avant.
 */
const EtapesBloc = ({ bloc }) => {
    const etapes = Array.isArray(bloc?.etapes) ? bloc.etapes : [];
    if (etapes.length === 0) return null;

    return (
        <section className="mb-10" data-testid="bloc-etapes">
            {(bloc.titre || bloc.sousTitre) && (
                <div>
                    {bloc.titre && (
                        <h2 className="text-xl font-bold text-gray-900">{bloc.titre}</h2>
                    )}
                    {bloc.sousTitre && (
                        <p className="text-sm text-gray-600 mt-1">{bloc.sousTitre}</p>
                    )}
                </div>
            )}

            <div className="divide-y divide-gray-100 border-t border-gray-100 mt-4">
                {etapes.map((e, i) => {
                    const Icone = iconeDe(e.icone);

                    return (
                        <div key={i} className="py-6 hover:bg-gray-50 transition-colors">
                            <div className="flex gap-4">
                                {/* LE RANG, dans la pastille de la charte. */}
                                <div className="flex-shrink-0">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cabaza-purple to-cabaza-pink flex items-center justify-center text-white font-bold">
                                        {i + 1}
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start gap-3 mb-2">
                                        {Icone && (
                                            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                <Icone className="w-4 h-4 text-gray-600" />
                                            </div>
                                        )}
                                        {e.titre && (
                                            <h3 className="text-lg font-semibold text-gray-900 pt-1">
                                                {e.titre}
                                            </h3>
                                        )}
                                    </div>

                                    {/* L'INDENTATION SUIT L'ICÔNE quand il y en a
                                        une, et disparaît sinon : un texte décalé
                                        sous rien paraîtrait mal aligné. */}
                                    {e.texte && (
                                        <p className={`text-gray-600 ${Icone ? 'ml-11' : ''}`}>
                                            {e.texte}
                                        </p>
                                    )}

                                    {e.astuce && (
                                        <div className={`mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg ${Icone ? 'ml-11' : ''}`}>
                                            <p className="text-sm text-amber-800">{e.astuce}</p>
                                        </div>
                                    )}

                                    {/* L'EMPLACEMENT RESERVE A UNE CAPTURE.
                                        Le fallback affiche un cadre en
                                        pointilles meme sans image : il annonce
                                        qu'une illustration viendra, et garde la
                                        hauteur de la page stable quand elle
                                        arrivera. `emplacementImage` le demande
                                        explicitement — un cadre vide qu'on n'a
                                        pas voulu serait juste un trou. */}
                                    {e.image ? (
                                        <img
                                            src={e.image.startsWith('http') || e.image.startsWith('/')
                                                ? e.image
                                                : `${FILES_BASE_URL}/${e.image}`}
                                            alt=""
                                            loading="lazy"
                                            className={`mt-4 rounded-xl border max-w-full h-auto ${Icone ? 'ml-11' : ''}`}
                                        />
                                    ) : e.emplacementImage ? (
                                        <div className={`mt-4 p-4 bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 ${Icone ? 'ml-11' : ''}`}>
                                            <p className="text-sm text-gray-500 text-center">
                                                {e.emplacementImage}
                                            </p>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export default EtapesBloc;
